import { BatchProcessArea } from "@fp/admin-api-client";
import { PageContentContainer } from "@fp/ui/components/base/page-layout/page-layout";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { BatchProcessCard } from "@/components/batch-process/batch-process-card";
import { useBatchProcesses } from "@/components/batch-process/batch-process-context";
import { getBatchDisplayName } from "@/components/batch-process/fsm-execute-sync-metadata";
import { AdminLayout } from "@/components/admin-layout";
import { AdminPageHeader } from "@/components/admin-page-header";
import { redirectUnlessCapability } from "@/lib/organization-capabilities";

export const Route = createFileRoute("/batch-processes")({
  beforeLoad: ({ context: { queryClient } }) =>
    redirectUnlessCapability(queryClient, "canViewBatchProcesses"),
  component: RouteComponent,
});

type StatusFilter = "all" | "inProgress" | "completed" | "withFailures";

const filterInputClass =
  "px-3 py-2 border border-primary rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500";

function RouteComponent() {
  const { batches } = useBatchProcesses();
  const [search, setSearch] = useState("");
  const [areaFilter, setAreaFilter] = useState<BatchProcessArea | "all">("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const filteredBatches = useMemo(() => {
    const query = search.trim().toLowerCase();

    return batches.filter((batch) => {
      if (areaFilter !== "all" && batch.area !== areaFilter) {
        return false;
      }
      if (query && !getBatchDisplayName(batch).toLowerCase().includes(query)) {
        return false;
      }
      if (statusFilter === "inProgress" && batch.isComplete) {
        return false;
      }
      if (statusFilter === "completed" && !batch.isComplete) {
        return false;
      }
      if (statusFilter === "withFailures" && batch.failedCount === 0) {
        return false;
      }
      return true;
    });
  }, [batches, search, areaFilter, statusFilter]);

  return (
    <AdminLayout>
      <AdminPageHeader
        title="Batch Processes"
        parent={{ url: "/dashboard", label: "Home" }}
      />
      <PageContentContainer>
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap gap-3">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name"
              className={filterInputClass}
            />
            <select
              value={areaFilter}
              onChange={(e) =>
                setAreaFilter(e.target.value as BatchProcessArea | "all")
              }
              className={filterInputClass}
            >
              <option value="all">All areas</option>
              <option value={BatchProcessArea.PayoutRequest}>Payout request</option>
              <option value={BatchProcessArea.Fsm}>FSM</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className={filterInputClass}
            >
              <option value="all">All statuses</option>
              <option value="inProgress">In progress</option>
              <option value="completed">Completed</option>
              <option value="withFailures">With failures</option>
            </select>
          </div>

          {filteredBatches.length === 0 ? (
            <p className="text-sm text-tertiary">No batch processes found.</p>
          ) : (
            <div className="flex max-w-2xl flex-col gap-2">
              {filteredBatches.map((batch) => (
                <BatchProcessCard
                  key={batch.id}
                  batch={batch}
                  isExpanded={expandedId === batch.id}
                  onToggleExpand={() =>
                    setExpandedId((id) => (id === batch.id ? null : batch.id))
                  }
                />
              ))}
            </div>
          )}
        </div>
      </PageContentContainer>
    </AdminLayout>
  );
}
